export default `# A Movie Page

The *Sweet Pumpkins* website works like a dream.

But wouldn't it be great if we could click on a movie thumbnail and learn more about it?

This is what we are going to implement in this chapter.

When a user visits our website we want them to be able to browse movies immediately. And when they decide to check movie details, the application should display a
different page, for example \`localhost:3000/movie/movie_id\`. Where the \`movie_id\` is an actual movie id from the TMDB API.

To implement this solution, we need a router.

The most popular router solution for React applications is React Router.

Let's add it to the application:

\`\`\`plainJavascript
yarn add react-router-dom
\`\`\`

Our \`App\` component always renders the same two components \`Header\` and \`Main\` at the moment:
\`\`\`plainJsx
// App.js

import React from "react";
import Header from "./header/Header";
import Main from "./main/Main";

const App = () => {
  return (
    <div>
      <Header />
      <Main />
    </div>
  );
};

export default App;
\`\`\`

But now, we want to render the \`Main\` component when a user visits the main route \`localhost:3000/\`
and the \`Movie\` component (it doesn't exist yet) when a user visits the movie details page \`localhost:3000/movie/movie_id\`.

We will wrap our application in the \`BrowserRouter\` component from the \`react-router-dom\` package and
we will use \`Route\` components to decide which component should be rendered:

\`\`\`plainJsx
// App.js

import React from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom"
import Header from "./header/Header";
import Main from "./main/Main";
import Movie from "./movie/Movie";

const App = () => {
  return (
    <BrowserRouter>
      <div>
        <Header />
        <Switch>
          <Route exact path='/' component={Main} />
          <Route path="/movies/:movieId" component={Movie} />
        </ Switch>
      </div>
    </BrowserRouter>
  );
};

export default App;
\`\`\`

In the code snippet above, we have used a named import (curly braces) to import three components from the \`react-router-dom\` package – \`BrowserRouter\`, \`Switch\` and \`Route\`

We have wrapped the application in the \`BrowserRouter\` component and added two \`Route\` components inside the \`Switch\` component.

Now, depending on which url you visit, the \`Switch\` and the \`Route\`s components will decide which content to display.

When you visit the root url, \`/localhost:3000/\`, they will display the \`Main\` component. But if you go to the \`/movies/:movieId\` url, they will display the \`Movie\` component.

If you want to learn more about the React Router, [here](https://reacttraining.com/react-router/web/guides/philosophy) you can find the full documentation for the package.

But what is the \`:movieId\` in the \`path\` pattern?

By adding a colon, we indicate that the word following the colon is a parameter.
Thanks to the React Router, we will be able to access this value from inside the \`Movie\` component.

When the React Router can't find a matching pattern for the route, it returns a \`404 Not Found\` error. To prevent the application from throwing an error
and displaying a blank page, let's add one more \`Route\` that will match all patterns and display the \`NotFound\` component:

\`\`\`plainJsx
// App.js

import React from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom"
import Header from "./header/Header";
import Main from "./main/Main";
import Movie from "./movie/Movie";
import NotFound from "./NotFound";

const App = () => {
  return (
    <BrowserRouter>
      <div>
        <Header />
        <Switch>
          <Route exact path='/' component={Main} />
          <Route path="/movie/:movieId" component={Movie} />
          <Route component={NotFound} />
        </ Switch>
      </div>
    </BrowserRouter>
  );
};

export default App;
\`\`\`

The \`Route\` without the \`path\` attribute matches all routes and ensures that the application we will display the \`NotFound\` component for all other routes.

Let's create the \`NotFound\` component. Place it in the \`src\` directory:

\`\`\`plainJsx
// NotFound.js

import React from "react";
import { Link } from "react-router-dom";

const NotFound = () => (
  <div>
    <h3>We couldn't find what you were looking for :(</h3>
    <Link to="/">Here you can browse the movies</Link>
  </div>
);

export default NotFound;
\`\`\`

The rendering of pages for not-found routes became an art in and of itself, for example [Mailchimp](https://mailchimp.com/movies).

In our case, we will leave the \`NotFound\` component as is.

## Movie Component

We haven't built a \`Movie\` component yet. Let's create a simple one and check if our routing solution works.

Create a new directory, \`movie\`, and create two files, *Movie.js* and *Movie.css*, inside.

\`\`\`plainJsx
// Movie.js

import React from "react";
import "./Movie.css"

class Movie extends React.Component {
  render() {
    return <div class="movie-page">Movie</div>;
  }
}

export default Movie;
\`\`\`

Now, when you visit your website at the \`localhost:3000/\`, it will render the \`Main\` component with a list of movies.

But when you go to the \`localhost:3000/movies/movie_id\` it will render a new \`Movie\` component.

Since we have tested that routes work, now we are going to turn the \`MovieListItem\` component into a clickable thumbnail.

We will use the \`Link\` component from the React Router to wrap the \`MovieListItem\`:

\`\`\`plainJsx
// MovieListItem.js

import React from "react";
import "./MovieListItem.css";
import { Link } from "react-router-dom";

const MovieListItem = ({ movie }) => {
  const { id, title, poster_path, release_year, vote_average } = movie;
  const imgUrl = \`https://image.tmdb.org/t/p/w342/\${poster_path}\`;

  return (
    <li className="movie">
      <Link to={\`/movies/\${id}\`} className="thumbnail">
        <img src={imgUrl} alt="" />
        <div className="movie-description">
          // code omited
        </div>
      </Link>
    </li>
  );
};

export default MovieListItem;
\`\`\`

The \`MovieListItem\` receives a \`movie\` object in \`props\`. We can pass the movie's \`id\` to the \`Link\` component.

Now, when we click on a movie with the 346364 \`id\`, thanks to the \`Link\`, we will go to the \`localhost:3000/movie/346364\` page.

Because we wrapped everything inside the \`li\` tag into the \`Link\` component, all the content will get the \`text-decoration: underline;\` attribute.
The \`Link\` component is just an \`a\` element with some superpowers.

Additionally, placing the \`Link\` between the \`.movie\` class and the children elements can break the \`flex-box\` model relationship between a flex container and its children – flex items.

That's why I added a \`thumbnail\` class to the \`Link\` and put additional CSS in the *MovieListItem.css*:

\`\`\`plainCss
// MovieListItem.css

.thumbnail {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  cursor: pointer;
  text-decoration: none;
}
\`\`\`


The React Router passes three new props: \`history\`, \`location\` and \`match\` to the rendered components.

Thanks to the \`match\` object we can easily access the route parameter – the part of the route pattern that starts with a colon.
In our case, it's the \`movieId\`.

The \`movieId\` is passed to the \`match\` object in the \`params\` object. We can access it using the \`match.params.movieId\`.

If our \`Movie\` component knows the \`movieId\`, it can fetch movie details from the TMDB API. Again, we will do so in the \`componentDidMout\` method.

To get movie details we will use the [different endpoint](https://developers.themoviedb.org/3/movies/get-movie-details) of the TMDB API:

\`\`\`plainJavascript
https://api.themoviedb.org/3/movie/{movie_id}?api_key=<<api_key>>&language=en-US
\`\`\`

The \`Movie\` component will fetch movie details and store them in its own \`state\`:

\`\`\`plainJsx
// Movie.js

import React from "react";

class Movie extends React.Component {
  state = {
    movie: {}
  }

  componentDidMount() {
    const { movieId } = this.props.match.params;
    const movieUrl = \`https://api.themoviedb.org/3/movie/\${movieId}?api_key=651925d45022d1ae658063b443c99784&language=en-US\`;
    fetch(movieUrl)
      .then(response => response.json())
      .then(data => this.setState({ movie: data }))
      .catch(error => console.log("Error:", error));
  }

  render() {
    return <div>Movie</div>;
  }
}

export default Movie;
\`\`\`

The \`Movie\` component should initialise the \`state\` with the \`movie\` property that contains an empty object.

The object will remain empty until we get movie data from the TMDB API. If we tried to display the \`title\`
or the \`vote_average\` in the \`render\` method before receiving the data
from the TMDB API, our application would break.

To avoid such a situation, the \`Movie\` component should know if it's still waiting for data or if data has arrived.
We will use the boolean \`isLoading\` variable, that will serve as a flag.
If \`isLoading\` is true, then the component is waiting for data. If the \`isLoading\` is false, the component has
received data and is ready to render information about the movie.

Let's add the new property \`isLoading\` to the \`state\` of the \`Movie\` container and set it to true.

To decide what to display in the render method we can use a ternary operator:
\`\`\`plainJavascript
// Movie.js

import React from "react";
import LoadingMovie from "./LoadingMovie";

class Movie extends React.Component {
  state = {
    isLoading: true,
    movie: {}
  }

  // omitted code

  render() {
    const { isLoading } = this.state;
    return (
     <div>
      {
        isLoading
          ? <LoadingMovie />
          : // display information about the movie
      }
     </div>
    )
  }
}

export default Movie;
\`\`\`

Now, our component will work properly even if the data about a movie has not arrived yet.

In such case, it will render the \`LoadingMovie\` component.

Let's add the \`LoadingMovie\` in the *movie* directory:
\`\`\`plainJsx
// LoadingMovie.js

import React from "react";

const LoadingMovie = () => <h2>Loading Movie</h2>

export default LoadingMovie;
\`\`\`

## Rendering Movie Data

We have the basic structure in place. Let's put movie data on a screen.

Instead of using a small image, which we used for movie thumbnails in the \`Movies\`, we will use
the \`backdrop_path\` to grab a nice big image.

To create the full image url from the \`backdrop_path\` we will use a string literal:
\`\`\`plainJavascript
\`http://image.tmdb.org/t/p/w1280/\${backdrop_path}\`
\`\`\`

Let's assume that we want to stretch the big image, so it takes the whole width of the screen, but we also want it to have a height of 575px.

If we used a regular \`img\` tag, the backdrop image would stretch vertically to 575px but its width
would grow in an unpredictable way.

A better technique is to use the \`div\` element and assign the big image url as its \`background-image\` property.

Let's see how it works:

\`\`\`plainJsx
// Movie.js

class Movie extends React.Component {
  // code omited

  render() {
    const backdropStyle = {
      backgroundImage: \`url(\${imgUrl})\`
    };

    return (
      <div>
        {
          this.state.isLoading
            ? <LoadingMovie />
            : <div className="movie-page">
                <div className="movie-backdrop" style={backdropStyle} />
              </div>
        }
      </div>
    );
  }
}
\`\`\`

and CSS in *Movie.css*:
\`\`\`plainCss
/* Movie.css */

.movie-page {
  display: flex;
  flex-direction: column;
}

.movie-page .movie-image {
  flex-basis: 100%;
  height: 575px;
  background-size: cover;
  background-position: center center;
}
\`\`\`

Most of the styling is done in the CSS file. But to set the \`backdrop_path\` as a background image, we have to use an inline style.
\`\`\`plainCss
const backgroundStyle = {
  backgroundImage: \`url(http://image.tmdb.org/t/p/w1280/\${movie.backdrop_path})\`
}
\`\`\`


Let's render the remaining movie data:
\`\`\`plainJsx
render() {
    const {
      title,
      backdrop_path,
      release_date,
      genres,
      overview,
      vote_average,
      runtime
    } = this.state.movie;

    const releaseYear = release_date ? release_date.substring(0, 4) : null;
    const imgUrl = \`http://image.tmdb.org/t/p/w1280/\${backdrop_path}\`;
    const backgroundStyle = {
      backgroundImage: \`url(\${imgUrl})\`
    };

    return (
      <div>
        {this.state.isLoading
          ? <LoadingMovie />
          : <div className="movie-page">
              <div className="movie-backdrop" style={backgroundStyle} />
              <div className="movie-details">
                <h1>
                  {title}
                  <span>({releaseYear})</span>
                </h1>
                <section className="genres">
                  {genres.map((genre, index) => (
                    <div key={genre.id}>
                      <span>{genre.name}</span>
                      {index < genres.length - 1 && (
                        <span className="separator">|</span>
                      )}
                    </div>
                  ))}
                </section>
                <h5>
                  Rating:
                  <span>{vote_average}</span>
                </h5>
                <h5>
                  Runtime:
                  <span>{\`\${runtime} min\`}</span>
                </h5>
                <h4>Overview</h4>
                <p>{overview}</p>
              </div>
            </div>
        }
      </div>
    );
  }
\`\`\`

- We have wrapped all movie data into a separate \`div\` with the class \`movie-details\` and placed it below the big image.
- We are using a destructuring assignment to get all data to display from the \`movie\` object in the \`state\`.
- If the \`release_date\` is \`undefined\` we will assign \`null\` to the \`year\` variable. Otherwise, we will grab the first 4 characters that represent the year.
- We have created a full \`imgUrl\` path and \`backdropStyle\` object to pass it as a background to the \`div\` with the \`movie-backdrop\` class.

If \`this.state.isLoading\` is \`true\`, the application will display the \`LoadingMovie\` component.
Otherwise, it will display all movie details.

Inside the \`movie-details\`, we have displayed the movie title and year of release. It should render as: War for the Planet of the Apes (2017).

The \`genres\` property of the movie is an array of objects. Every object has two properties: \`id\` and \`name\`, for example \`[{id: 39, name: "Drama"}, {id: 284, name: "Criminal"}]\`.

That's why we have used the \`map\` method to iterate through an array of genres to display all genres. We want also to display genres separated by \`|\`.
\`\`\`plainJavascript
Drama | Criminal | Thriller
\`\`\`

Every genre, except the last one, should have a \`|\` on its right. Therefore, we are checking if we have reached
the end of the \`genres\` array: \`index < genres.length - 1\`.

Next, we use the \`&&\` logical operator to decide if we should display the separator. To display the component only when a certain condition is met
we can use this technique:
\`\`\`plainJavascript
condition && <Component />
\`\`\`

In such case, only if the condition evaluates to \`true\` will the component be displayed.

I hope that the rest of the component code is self-explanatory. Let's analyse the styles now:

\`\`\`plainCss
/* Movie.css */

.movie-page {
  display: flex;
  flex-direction: column;
}

.movie-page h5 {
  color: #888;
  font-weight: normal;
  line-height: 1.25rem;
}

.movie-page h5 span {
  font-size: inherit;
  font-weight: normal;
  color: black;
  padding-left: 1rem;
}

.movie-page h4 {
  margin: 0;
  font-size: 1.25rem;
  line-height: 2;
}

.movie-page p {
  color: #888;
  line-height: 1.5;
}

.movie-page .movie-image {
  flex-basis: 100%;
  height: 575px;
  background-size: cover;
  background-position: center center;
}

.movie-page .movie-details {
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: 20px auto 60px auto;
}

.movie-page .movie-details h1 {
  line-height: 1.5em;
}

.movie-page .movie-details h1 span {
  font-size: inherit;
  font-weight: normal;
  padding-left: 1rem;
  color: #bbb;
  line-height: inherit;
}

.movie-page .genres {
  display: flex;
  margin-bottom: 1rem;
}

.movie-page .genres span {
  font-weight: normal;
}

.movie-page .separator {
  color: lightgray;
  padding: 0 10px;
}
\`\`\`

When using styles in *Movie.css* remember to be specific. We have used the *movie-details* class before in the *MovieList.css*.

That's why, if we want to use the \`movie-details\` class again, we have to precede the \`movie-details\` class with the \`movie-page\`:
\`\`\`plainCss
.movie-page .movie-details {
  /* styles here */
}
\`\`\`

The *movie-details* class sets the \`width\` of the container to 800px. If we want to center it on the screen horizontally we can use
\`margin: top-and-bottom auto;\` or be more specific and set on top, right, bottom and left margins: \`margin: 20px auto 60px auto;\`.

In both cases, left and right margins are set to \`auto\` and \`auto\` will make them equal, meaning it will center the \`div\` horizontally on a screen.

When setting the \`span\`'s font sizes, we use \`inherit\` keyword. The reason for that is that we want \`span\`s to have the same font size
as the header tag, they are wrapped in.

Here is the final code:

\`\`\`codeSandbox
mjl8jj4yny
\`\`\`

`;



// WEBPACK FOOTER //
// ./src/book/chapters/a-movie-page.js